import { useState, useEffect } from 'react';
import Modal from '../common/Modal';
import { Button } from '../common/Button';
import { EnvelopeIcon, SparklesIcon, PaperAirplaneIcon, XMarkIcon, ArrowUturnLeftIcon, ArrowUturnRightIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import toast from 'react-hot-toast';

type Attachment = {
  filename: string;
  path: string;
  size: number;
  mimetype: string;
};

type ClientMessage = {
  id: number;
  transport_id: number;
  carrier_id?: number; // Track which carrier offer this relates to
  direction: 'outgoing' | 'incoming';
  sender_email?: string;
  recipient_email?: string;
  subject?: string;
  body?: string;
  attachments?: Attachment[];
  sent_at?: string;
  created_at?: string;
};

type CarrierInvitation = {
  carrier_id: number;
  carrier_name: string;
  status?: string;
};

type Transport = {
  id: number;
  reference?: string;
  status: string;
  client_email?: string;
  client_name?: string;
  client_surname?: string;
  client_company?: string;
  carrier_invitations?: CarrierInvitation[];
};

interface ClientConversationModalProps {
  isOpen: boolean;
  onClose: () => void;
  transport: Transport | null;
  onCreateOffer: () => void;
}

type MessageThread = {
  carrierId: number | null;
  carrierName: string | null;
  messages: ClientMessage[];
  latestMessage: ClientMessage;
  count: number;
};

export const ClientConversationModal = ({
  isOpen,
  onClose,
  transport,
  onCreateOffer
}: ClientConversationModalProps) => {
  const [messages, setMessages] = useState<ClientMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedThread, setSelectedThread] = useState<number | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [replyMode, setReplyMode] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [composeMode, setComposeMode] = useState(false);
  const [messageSubject, setMessageSubject] = useState('');
  const [messageBody, setMessageBody] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (isOpen && transport) {
      loadClientMessages();
      setMessageSubject(`Dodatkowe informacje - Transport #${transport.id}`);
    }
  }, [isOpen, transport]);

  useEffect(() => {
    // Auto-select first thread if exists
    if (messages.length > 0 && selectedThread === null && !composeMode) {
      const threads = createMessageThreads(messages);
      if (threads.length > 0) {
        setSelectedThread(threads[0].carrierId);
      }
    }
  }, [messages, selectedThread, composeMode]);

  const loadClientMessages = async () => {
    if (!transport) return;

    try {
      setLoading(true);
      const response = await api.get(`/transport/${transport.id}/client-messages`);
      const msgs = response.data.data || [];

      // Sort by newest first
      const sortedMsgs = msgs.sort((a: ClientMessage, b: ClientMessage) => {
        const dateA = new Date(a.sent_at || a.created_at || 0).getTime();
        const dateB = new Date(b.sent_at || b.created_at || 0).getTime();
        return dateB - dateA;
      });

      setMessages(sortedMsgs);
    } catch (error: any) {
      console.error('Failed to load client messages:', error);
      toast.error('Nie udało się załadować wiadomości do klienta');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadClientMessages();
    setRefreshing(false);
    toast.success('Wiadomości odświeżone');
  };

  const handleSendMessage = async () => {
    if (!messageBody.trim() || !transport.client_email) {
      toast.error('Napisz treść wiadomości i upewnij się, że klient ma email');
      return;
    }

    try {
      setSending(true);
      await api.post(`/transport/${transport.id}/send-client-message`, {
        subject: messageSubject.trim(),
        body: messageBody,
        recipient_email: transport.client_email
      });

      toast.success('Wiadomość wysłana');
      setComposeMode(false);
      setMessageBody('');
      await loadClientMessages();
    } catch (error: any) {
      console.error('Failed to send message:', error);
      toast.error(error?.response?.data?.error || 'Nie udało się wysłać wiadomości');
    } finally {
      setSending(false);
    }
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || selectedThreadMessages.length === 0) {
      toast.error('Napisz treść odpowiedzi');
      return;
    }

    const latestMessage = selectedThreadMessages[selectedThreadMessages.length - 1];

    try {
      setSending(true);
      await api.post(`/transport/${transport.id}/reply-client-message`, {
        messageId: latestMessage.id,
        replyText: replyText.trim(),
        carrier_id: selectedThread
      });

      toast.success('Odpowiedź wysłana');
      setReplyMode(false);
      setReplyText('');
      await loadClientMessages();
    } catch (error: any) {
      console.error('Failed to send reply:', error);
      toast.error(error?.response?.data?.error || 'Nie udało się wysłać odpowiedzi');
    } finally {
      setSending(false);
    }
  };

  if (!transport) return null;

  const clientDisplayName = transport.client_company ||
    `${transport.client_name || ''} ${transport.client_surname || ''}`.trim() ||
    transport.client_email ||
    'Klient';

  // Get carrier name by ID
  const getCarrierName = (carrierId?: number | null): string | null => {
    if (!carrierId || !transport.carrier_invitations) return null;
    const carrier = transport.carrier_invitations.find(c => c.carrier_id === carrierId);
    return carrier ? carrier.carrier_name : null;
  };

  // Group messages by carrier_id into threads
  const createMessageThreads = (msgs: ClientMessage[]): MessageThread[] => {
    // Group by carrier_id
    const grouped = msgs.reduce((acc, msg) => {
      const key = msg.carrier_id || 0; // Use 0 for unknown carriers
      if (!acc[key]) acc[key] = [];
      acc[key].push(msg);
      return acc;
    }, {} as Record<number, ClientMessage[]>);

    // Transform into thread objects
    const threads = Object.entries(grouped).map(([carrierIdStr, threadMessages]) => {
      const carrierId = parseInt(carrierIdStr);
      const carrierName = getCarrierName(carrierId === 0 ? null : carrierId);

      // Sort messages by date (newest first to find latest)
      const sortedMsgs = threadMessages.sort((a, b) => {
        const dateA = new Date(a.sent_at || a.created_at || 0).getTime();
        const dateB = new Date(b.sent_at || b.created_at || 0).getTime();
        return dateB - dateA;
      });

      return {
        carrierId: carrierId === 0 ? null : carrierId,
        carrierName,
        messages: sortedMsgs,
        latestMessage: sortedMsgs[0],
        count: sortedMsgs.length
      };
    });

    // Sort threads by latest message date (newest first)
    threads.sort((a, b) => {
      const dateA = new Date(a.latestMessage.sent_at || a.latestMessage.created_at || 0).getTime();
      const dateB = new Date(b.latestMessage.sent_at || b.latestMessage.created_at || 0).getTime();
      return dateB - dateA;
    });

    return threads;
  };

  const messageThreads = createMessageThreads(messages);

  // Get messages for selected thread (sorted chronologically for conversation view)
  const selectedThreadMessages = selectedThread !== null
    ? messageThreads.find(t => t.carrierId === selectedThread)?.messages.sort((a, b) => {
        const dateA = new Date(a.sent_at || a.created_at || 0).getTime();
        const dateB = new Date(b.sent_at || b.created_at || 0).getTime();
        return dateA - dateB; // Oldest first for conversation view
      }) || []
    : [];

  // Strip HTML tags for preview
  const stripHtml = (html: string) => {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Konwersacja z klientem - Zlecenie #${transport.id}`}
      size="full"
    >
      <div className="flex h-[70vh]">
        {/* LEFT SIDEBAR - Message List */}
        <div className="w-1/3 border-r border-gray-300 flex flex-col">
          {/* Header */}
          <div className="p-4 border-b border-gray-300 bg-gray-50">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-gray-800">
                Konwersacje ({messageThreads.length})
              </h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleRefresh}
                  disabled={refreshing}
                  className="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-200 rounded-md transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Odśwież wiadomości"
                >
                  <svg className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span>{refreshing ? 'Sprawdzanie...' : 'Odśwież'}</span>
                </button>
                <Button
                  size="sm"
                  onClick={() => {
                    setComposeMode(true);
                    setSelectedThread(null);
                    setReplyMode(false);
                  }}
                  className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-1"
                >
                  <PaperAirplaneIcon className="w-4 h-4" />
                  Nowa
                </Button>
              </div>
            </div>

            {/* Client Info */}
            <div className="text-sm text-gray-700 bg-white p-2 rounded border border-gray-200">
              <div className="font-medium">{clientDisplayName}</div>
              {transport.client_email && (
                <div className="text-xs text-gray-600 mt-0.5">{transport.client_email}</div>
              )}
            </div>
          </div>

          {/* Thread List */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center text-gray-500">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3"></div>
                <p className="text-sm">Ładowanie wiadomości...</p>
              </div>
            ) : messageThreads.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <EnvelopeIcon className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p>Brak wiadomości</p>
                <p className="text-sm mt-1">Utwórz pierwszą ofertę dla klienta</p>
              </div>
            ) : (
              messageThreads.map(thread => {
                const isSelected = selectedThread === thread.carrierId;
                const latestMsg = thread.latestMessage;
                const date = new Date(latestMsg.sent_at || latestMsg.created_at || '');
                const bodyPreview = latestMsg.body ? stripHtml(latestMsg.body) : '';

                return (
                  <div
                    key={thread.carrierId || 'unknown'}
                    onClick={() => {
                      setSelectedThread(thread.carrierId);
                    }}
                    className={`p-4 border-b border-gray-200 cursor-pointer transition-colors ${
                      isSelected
                        ? 'bg-blue-100 border-l-4 border-l-blue-600'
                        : 'hover:bg-gray-50 border-l-4 border-l-transparent'
                    }`}
                  >
                    {/* Carrier Name & Message Count */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <EnvelopeIcon className="w-5 h-5 text-blue-600 flex-shrink-0" />
                        <span className="font-bold text-sm text-gray-900 truncate">
                          {thread.carrierName || 'Nieznany przewoźnik'}
                        </span>
                      </div>
                      <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full font-medium flex-shrink-0 ml-2">
                        {thread.count}
                      </span>
                    </div>

                    {/* Latest Message Subject */}
                    <div className="font-medium text-sm text-gray-900 mb-1 truncate">
                      {latestMsg.subject || '(Brak tematu)'}
                    </div>

                    {/* Latest Message Preview */}
                    <div className="text-xs text-gray-600 mb-2 line-clamp-2">
                      {truncateText(bodyPreview, 80)}
                    </div>

                    {/* Date */}
                    <div className="text-xs text-gray-500">
                      {date.toLocaleString('pl-PL', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Create Offer Button */}
          <div className="p-4 border-t border-gray-300 bg-gray-50">
            <Button
              onClick={onCreateOffer}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white flex items-center justify-center gap-2"
            >
              <SparklesIcon className="w-5 h-5" />
              Utwórz ofertę dla klienta
            </Button>
          </div>
        </div>

        {/* RIGHT PANEL - Message Details or Compose */}
        <div className="flex-1 flex flex-col">
          {composeMode ? (
            /* COMPOSE MODE */
            <div className="flex-1 flex flex-col">
              {/* Header */}
              <div className="p-4 border-b border-gray-300 bg-blue-50">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-800">Nowa wiadomość do klienta</h3>
                  <button
                    onClick={() => {
                      setComposeMode(false);
                      setMessageBody('');
                      if (messageThreads.length > 0) {
                        setSelectedThread(messageThreads[0].carrierId);
                      }
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {/* Compose Form */}
              <div className="flex-1 p-6 overflow-y-auto bg-white">
                <div className="space-y-4 max-w-3xl">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Do
                    </label>
                    <input
                      type="text"
                      value={transport.client_email || ''}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Temat
                    </label>
                    <input
                      type="text"
                      value={messageSubject}
                      onChange={(e) => setMessageSubject(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Temat wiadomości"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Treść wiadomości
                    </label>
                    <textarea
                      value={messageBody}
                      onChange={(e) => setMessageBody(e.target.value)}
                      className="w-full h-64 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Treść wiadomości..."
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={handleSendMessage}
                      disabled={sending || !messageBody.trim()}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      {sending ? 'Wysyłanie...' : 'Wyślij wiadomość'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setComposeMode(false);
                        setMessageBody('');
                        if (messageThreads.length > 0) {
                          setSelectedThread(messageThreads[0].carrierId);
                        }
                      }}
                      disabled={sending}
                    >
                      Anuluj
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ) : selectedThread !== null && selectedThreadMessages.length > 0 ? (
            /* THREAD CONVERSATION VIEW */
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Header */}
              <div className="p-4 border-b border-gray-300 bg-gray-50">
                <div className="space-y-2">
                  <div className="text-lg font-semibold text-gray-900">
                    {getCarrierName(selectedThread) || 'Nieznany przewoźnik'}
                  </div>
                  <div className="text-sm text-gray-600">
                    {selectedThreadMessages.length} {selectedThreadMessages.length === 1 ? 'wiadomość' : 'wiadomości'}
                  </div>
                </div>
              </div>

              {/* Action Toolbar */}
              <div className="px-4 py-2 border-b border-gray-200 bg-white flex items-center gap-2">
                <button
                  onClick={() => setReplyMode(true)}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                  title="Odpowiedz"
                >
                  <ArrowUturnLeftIcon className="w-4 h-4" />
                  <span>Odpowiedz</span>
                </button>
                <button
                  onClick={() => {
                    setComposeMode(true);
                    setSelectedThread(null);
                    setReplyMode(false);
                  }}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                  title="Przekaż"
                >
                  <ArrowUturnRightIcon className="w-4 h-4" />
                  <span>Przekaż</span>
                </button>
              </div>

              {/* Thread Messages with proper scroll */}
              <div className="flex-1 overflow-y-auto bg-gray-50">
                {!replyMode ? (
                <div className="p-6 space-y-4">
                  {selectedThreadMessages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`p-4 rounded-lg shadow-sm ${
                        msg.direction === 'outgoing'
                          ? 'bg-blue-50 border-l-4 border-blue-500'
                          : 'bg-green-50 border-l-4 border-green-500'
                      }`}
                    >
                      {/* Message Header */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          {msg.direction === 'outgoing' ? (
                            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                            </svg>
                          ) : (
                            <EnvelopeIcon className="w-4 h-4 text-green-600" />
                          )}
                          <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                            msg.direction === 'outgoing'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-green-100 text-green-700'
                          }`}>
                            {msg.direction === 'outgoing' ? 'Wysłana' : 'Otrzymana'}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500">
                          {new Date(msg.sent_at || msg.created_at || '').toLocaleString('pl-PL', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                      </div>

                      {/* Subject */}
                      <div className="font-medium text-sm text-gray-900 mb-2">
                        {msg.subject || '(Brak tematu)'}
                      </div>

                      {/* From/To */}
                      <div className="text-xs text-gray-600 mb-3">
                        <span className="font-medium">
                          {msg.direction === 'outgoing' ? 'Do: ' : 'Od: '}
                        </span>
                        {msg.direction === 'outgoing'
                          ? msg.recipient_email
                          : msg.sender_email}
                      </div>

                      {/* Body */}
                      <div
                        className="prose prose-sm max-w-none text-gray-800"
                        dangerouslySetInnerHTML={{ __html: msg.body || '<p class="text-gray-500 italic">Brak treści</p>' }}
                      />

                      {/* Attachments */}
                      {msg.attachments && msg.attachments.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <div className="text-xs font-medium text-gray-700 mb-2">
                            Załączniki ({msg.attachments.length}):
                          </div>
                          <div className="space-y-1">
                            {msg.attachments.map((att, attIdx) => (
                              <a
                                key={attIdx}
                                href={`${import.meta.env.VITE_API_URL || 'http://localhost:8000'}${att.path}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 p-2 bg-gray-100 hover:bg-gray-200 rounded text-sm transition-colors"
                              >
                                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                </svg>
                                <span className="font-medium text-blue-600">{att.filename}</span>
                                <span className="text-gray-500">({formatFileSize(att.size)})</span>
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                ) : (
                  /* Reply Form */
                  <div className="p-6">
                    <div className="mb-4">
                      <div className="text-sm font-medium text-gray-700 mb-2">
                        Odpowiadasz na ostatnią wiadomość:
                      </div>
                      <div
                        className="prose prose-sm max-w-none text-gray-800 mb-4 p-4 bg-white border-l-4 border-blue-500 rounded"
                        dangerouslySetInnerHTML={{
                          __html: selectedThreadMessages[selectedThreadMessages.length - 1].body ||
                          '<p class="text-gray-500 italic">Brak treści</p>'
                        }}
                      />
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Twoja odpowiedź
                        </label>
                        <textarea
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          className="w-full h-48 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Napisz odpowiedź..."
                          autoFocus
                        />
                      </div>

                      <div className="flex gap-2">
                        <Button
                          onClick={handleSendReply}
                          disabled={sending || !replyText.trim()}
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          {sending ? 'Wysyłanie...' : 'Wyślij odpowiedź'}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setReplyMode(false);
                            setReplyText('');
                          }}
                          disabled={sending}
                        >
                          Anuluj
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* NO MESSAGE SELECTED */
            <div className="flex-1 flex items-center justify-center bg-gray-50">
              <div className="text-center text-gray-500">
                <EnvelopeIcon className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                <p className="text-lg font-medium">Wybierz wątek z listy</p>
                <p className="text-sm mt-1">lub utwórz nową wiadomość</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};
